import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Eraser, PenLine, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/SignaturePad";
import { errMsg, fmtDateTime } from "@/lib/api";

const Check = ({ id, checked, onChange, children }) => (
  <label className="flex items-start gap-3 text-sm cursor-pointer">
    <input type="checkbox" data-testid={id} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#0B6E8F]" />
    <span className="text-[#0D2B3A] leading-snug">{children}</span>
  </label>
);

export function SignedBox({ sig, label }) {
  return (
    <div className="rounded-lg border border-[#BBF7D0] bg-[#F0FDF4] p-4" data-testid={`signed-box-${sig.role}`}>
      <div className="flex items-center gap-2 text-xs font-semibold text-[#166534] uppercase tracking-wider"><CheckCircle2 size={14} /> {label}</div>
      <img src={sig.image} alt="firma" className="h-20 mt-2 object-contain" />
      <div className="text-xs text-[#47606B]">{sig.signer_name} • {fmtDateTime(sig.signed_at)} • {sig.method === "remoto" ? "da remoto" : "in presenza"}</div>
    </div>
  );
}

export function SignBlock({ pageKey, role, label, defaultName, needPrivacy, onSign, lastImage, onDrawn }) {
  const pad = useRef(null);
  const [name, setName] = useState(defaultName || "");
  const [read, setRead] = useState(false);
  const [spec, setSpec] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [empty, setEmpty] = useState(true);
  const [busy, setBusy] = useState(false);
  const isSpec = role.endsWith("_spec");
  const ready = !empty && read && name.trim().length > 1 && (!isSpec || spec) && (!needPrivacy || privacy);

  const submit = async () => {
    setBusy(true);
    const image = pad.current.toDataURL();
    try {
      await onSign({ page_key: pageKey, role, image, signer_name: name.trim(), read_confirmed: read, specific_approval: spec, privacy_ack: privacy });
      onDrawn(role, image);
      toast.success("Firma apposta");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#D4E6EC] bg-white p-4 sm:p-5 space-y-4" data-testid={`sign-block-${role}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-serif-it text-lg font-semibold">{label}</div>
        <input data-testid={`signer-name-${role}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome e cognome del firmatario"
          className="rounded-md border border-[#D4E6EC] px-3 py-1.5 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-[#0B6E8F]/30" />
      </div>
      <SignaturePad ref={pad} testId={`signature-canvas-${role}`} onChange={setEmpty} />
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" data-testid={`signature-clear-${role}`} onClick={() => pad.current.clear()}><Eraser size={14} className="mr-1" /> Pulisci</Button>
        {lastImage && <Button type="button" variant="outline" size="sm" data-testid={`signature-reuse-${role}`} onClick={() => pad.current.fromDataURL(lastImage)}><RotateCcw size={14} className="mr-1" /> Riusa la firma appena tracciata</Button>}
      </div>
      <div className="space-y-2.5">
        <Check id={`read-confirm-${role}`} checked={read} onChange={setRead}>Dichiaro di aver letto integralmente il contenuto di questo foglio e di accettarlo.</Check>
        {isSpec && <Check id={`specific-approval-${role}`} checked={spec} onChange={setSpec}>Ai sensi degli artt. 1341 e 1342 c.c. approvo specificamente le clausole di cui agli artt. 3, 4, 5, 7, 9 e 10 delle Condizioni Generali.</Check>}
        {needPrivacy && <Check id={`privacy-ack-${role}`} checked={privacy} onChange={setPrivacy}>Dichiaro di aver ricevuto l'informativa privacy ex artt. 13-14 Reg. UE 2016/679 (art. 16).</Check>}
      </div>
      <Button data-testid={`signature-submit-${role}`} disabled={!ready || busy} onClick={submit} className="rounded-full px-6">
        <PenLine size={15} className="mr-2" /> {busy ? "Registrazione…" : "Apponi firma su questo foglio"}
      </Button>
    </div>
  );
}
